import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Sparkles, Check, Loader2 } from "lucide-react";

export default function ContextBrain() {
  const { workspaceId } = useParams();
  const [ws, setWs] = useState(null);
  const [tab, setTab] = useState("identity");

  const load = () => apiGet(`/workspaces/${workspaceId}`).then(setWs);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  if (!ws) return <Layout title="Context Brain"><div className="overline">loading…</div></Layout>;

  return (
    <Layout title="Context Brain" subtitle={ws.name}>
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList
          data-testid="cb-tabs"
          className="bg-zinc-900 border border-zinc-800 rounded-md p-1 mb-6"
        >
          {[
            ["identity", "Identity & ICP"],
            ["voices", "Voice Profiles"],
            ["voice", "Voice Samples"],
            ["signals", "ICP Signal Map"],
            ["knowledge", "Knowledge"],
          ].map(([k, l]) => (
            <TabsTrigger
              key={k}
              value={k}
              data-testid={`cb-tab-${k}`}
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-50 text-zinc-400 rounded-sm px-3 py-1.5 text-sm"
            >
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="identity">
          <IdentitySection ws={ws} workspaceId={workspaceId} onSaved={load} />
        </TabsContent>
        <TabsContent value="voices">
          <VoiceProfilesSection workspaceId={workspaceId} />
        </TabsContent>
        <TabsContent value="voice">
          <VoiceSection workspaceId={workspaceId} />
        </TabsContent>
        <TabsContent value="signals">
          <SignalsSection workspaceId={workspaceId} />
        </TabsContent>
        <TabsContent value="knowledge">
          <KnowledgeSection workspaceId={workspaceId} />
        </TabsContent>
      </Tabs>
    </Layout>
  );
}

function IdentitySection({ ws, workspaceId, onSaved }) {
  const [f, setF] = useState({
    name: ws.name,
    company: ws.company || "",
    product: ws.product || "",
    linkedin_url: ws.linkedin_url || "",
    icp_description: ws.icp_description || "",
    voice_notes: ws.voice_notes || "",
    product_features: (ws.product_features?.length ? ws.product_features : [""]),
    pain_points: (ws.pain_points?.length ? ws.pain_points : [""]),
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await apiPatch(`/workspaces/${workspaceId}`, {
        ...f,
        product_features: f.product_features.filter((x) => x.trim()),
        pain_points: f.pain_points.filter((x) => x.trim()),
      });
      toast.success("Saved");
      onSaved?.();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="surface p-5 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Workspace name">
            <Input
              data-testid="cb-name"
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
            />
          </Field>
          <Field label="Company">
            <Input
              data-testid="cb-company"
              value={f.company}
              onChange={(e) => setF({ ...f, company: e.target.value })}
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
            />
          </Field>
          <Field label="Product">
            <Input
              data-testid="cb-product"
              value={f.product}
              onChange={(e) => setF({ ...f, product: e.target.value })}
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
            />
          </Field>
          <Field label="LinkedIn URL">
            <Input
              data-testid="cb-linkedin"
              value={f.linkedin_url}
              onChange={(e) => setF({ ...f, linkedin_url: e.target.value })}
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
            />
          </Field>
        </div>
        <Field label="ICP description">
          <Textarea
            data-testid="cb-icp"
            rows={4}
            value={f.icp_description}
            onChange={(e) => setF({ ...f, icp_description: e.target.value })}
            className="bg-zinc-900 border-zinc-800 text-zinc-100"
          />
        </Field>
        <Field label="Voice notes">
          <Textarea
            data-testid="cb-voice-notes"
            rows={4}
            value={f.voice_notes}
            onChange={(e) => setF({ ...f, voice_notes: e.target.value })}
            className="bg-zinc-900 border-zinc-800 text-zinc-100"
          />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="surface p-5">
          <div className="overline mb-3">product features</div>
          <ListEditor
            testidPrefix="feat"
            items={f.product_features}
            onChange={(i, v) =>
              setF({ ...f, product_features: f.product_features.map((x, j) => (j === i ? v : x)) })
            }
            onAdd={() => setF({ ...f, product_features: [...f.product_features, ""] })}
            onRemove={(i) =>
              setF({ ...f, product_features: f.product_features.filter((_, j) => j !== i) })
            }
            placeholder="Feature…"
          />
        </div>
        <div className="surface p-5">
          <div className="overline mb-3">customer pain points</div>
          <ListEditor
            testidPrefix="pain"
            items={f.pain_points}
            onChange={(i, v) =>
              setF({ ...f, pain_points: f.pain_points.map((x, j) => (j === i ? v : x)) })
            }
            onAdd={() => setF({ ...f, pain_points: [...f.pain_points, ""] })}
            onRemove={(i) => setF({ ...f, pain_points: f.pain_points.filter((_, j) => j !== i) })}
            placeholder="Pain point…"
          />
        </div>
      </div>

      <div>
        <Button onClick={save} disabled={saving} className="btn-primary" data-testid="cb-save-identity">
          <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function VoiceSection({ workspaceId }) {
  const [items, setItems] = useState([]);
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [draft, setDraft] = useState("");
  const [source, setSource] = useState("linkedin_post");
  const [vpId, setVpId] = useState("");

  const load = async () => {
    apiGet(`/workspaces/${workspaceId}/voice_samples`).then(setItems).catch(() => setItems([]));
    apiGet(`/workspaces/${workspaceId}/voice_profiles`).then((vps) => {
      setVoiceProfiles(vps);
      if (!vpId && vps.length > 0) {
        const def = vps.find((v) => v.is_default) || vps[0];
        setVpId(def.id);
      }
    }).catch(() => {});
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const add = async () => {
    if (!draft.trim()) return;
    try {
      await apiPost(`/workspaces/${workspaceId}/voice_samples`, {
        sample_text: draft.trim(),
        source,
        tone_tags: [],
        voice_profile_id: vpId || null,
      });
      setDraft("");
      toast.success("Voice sample added");
      load();
    } catch (e) {
      toast.error("Failed to add");
    }
  };

  const remove = async (id) => {
    await apiDelete(`/workspaces/${workspaceId}/voice_samples/${id}`);
    load();
  };

  const filtered = vpId ? items.filter((s) => !s.voice_profile_id || s.voice_profile_id === vpId) : items;

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="surface p-5">
        <div className="overline mb-3">add voice sample</div>
        {voiceProfiles.length > 1 && (
          <div className="mb-3">
            <Label className="text-xs text-zinc-300 mb-1.5 block">Voice profile</Label>
            <select
              data-testid="voice-sample-vp"
              value={vpId}
              onChange={(e) => setVpId(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 text-zinc-200 rounded-md px-3 py-2 text-sm w-full"
            >
              {voiceProfiles.map((vp) => (
                <option key={vp.id} value={vp.id}>
                  {vp.name} {vp.is_default ? "(default)" : ""}
                </option>
              ))}
            </select>
          </div>
        )}
        <Textarea
          data-testid="voice-sample-input"
          rows={5}
          placeholder="Paste a real LinkedIn post, call transcript excerpt, or Slack message that perfectly captures the voice…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="bg-zinc-900 border-zinc-800 text-zinc-100 mb-3"
        />
        <div className="flex items-center gap-2">
          <select
            data-testid="voice-source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm rounded-md px-3 py-2"
          >
            <option value="linkedin_post">LinkedIn post</option>
            <option value="call_transcript">Call transcript</option>
            <option value="manual">Manual</option>
          </select>
          <Button onClick={add} className="btn-primary" data-testid="voice-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add sample
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="surface p-8 text-center">
            <div className="overline mb-1">no voice samples yet</div>
            <p className="text-sm text-zinc-400">
              Add 3-5 high-quality samples per voice profile. The AI uses these to match tone.
            </p>
          </div>
        ) : (
          filtered.map((it) => (
            <div key={it.id} className="surface p-4 fade-up" data-testid={`voice-item-${it.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <span className="overline" style={{ fontSize: 9 }}>
                  {it.source || "manual"}
                </span>
                <button
                  onClick={() => remove(it.id)}
                  className="text-zinc-500 hover:text-red-400 transition-colors"
                  data-testid={`voice-delete-${it.id}`}
                  aria-label="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{it.sample_text}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function VoiceProfilesSection({ workspaceId }) {
  const [profiles, setProfiles] = useState([]);
  const [creating, setCreating] = useState(false);
  const [creatingStep, setCreatingStep] = useState(0);
  const [newF, setNewF] = useState({ name: "", linkedin_url: "", autofill: true });

  const STEPS = [
    "Connecting to LinkedIn via Apify…",
    "Pulling recent 5 posts…",
    "Analyzing tone with Claude Sonnet 4.5…",
    "Saving voice profile + samples…",
  ];

  useEffect(() => {
    if (!creating) return;
    setCreatingStep(0);
    const t = setInterval(() => {
      setCreatingStep((s) => Math.min(s + 1, STEPS.length - 1));
    }, 12000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creating]);

  const load = () => apiGet(`/workspaces/${workspaceId}/voice_profiles`).then(setProfiles);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const isCompany = /linkedin\.com\/company\//i.test(newF.linkedin_url);

  const create = async () => {
    if (!newF.name.trim()) {
      toast.error("Name required (e.g. 'LeanSuite Company Page')");
      return;
    }
    setCreating(true);
    try {
      const res = await apiPost(`/workspaces/${workspaceId}/voice_profiles`, {
        name: newF.name.trim(),
        linkedin_url: newF.linkedin_url.trim(),
        autofill: newF.autofill && !!newF.linkedin_url.trim(),
        is_default: false,
      });
      setNewF({ name: "", linkedin_url: "", autofill: true });
      const sc = res.samples_count ?? 0;
      if (newF.autofill && newF.linkedin_url && sc === 0) {
        toast.warning(
          "Profile created — but no posts were retrievable from that URL. Add samples manually below.",
        );
      } else {
        toast.success(
          newF.autofill && newF.linkedin_url
            ? "Voice profile added with auto-analyzed samples"
            : "Voice profile added",
        );
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="surface p-5">
        <div className="overline mb-3">add a voice profile</div>
        <p className="text-xs text-zinc-400 mb-4 max-w-2xl">
          Use multiple voice profiles when you engage from different personas (e.g.{" "}
          <span className="text-zinc-200">Angad Personal</span> vs.{" "}
          <span className="text-zinc-200">LeanSuite Company Page</span>). Each has its own tone &amp;
          samples; product features and ICP signals stay shared.
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-zinc-300 mb-1.5 block">Voice profile name</Label>
            <Input
              data-testid="vp-name"
              value={newF.name}
              onChange={(e) => setNewF({ ...newF, name: e.target.value })}
              placeholder="e.g. LeanSuite Company Page"
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
              disabled={creating}
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-300 mb-1.5 block">LinkedIn URL</Label>
            <Input
              data-testid="vp-linkedin"
              value={newF.linkedin_url}
              onChange={(e) => setNewF({ ...newF, linkedin_url: e.target.value })}
              placeholder="https://linkedin.com/in/<handle>"
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
              disabled={creating}
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-300 mb-1.5 block">Auto-analyze?</Label>
            <button
              type="button"
              onClick={() => setNewF({ ...newF, autofill: !newF.autofill })}
              data-testid="vp-autofill"
              disabled={creating}
              className={`w-full text-sm px-3 py-2 rounded-md border ${
                newF.autofill
                  ? "border-sky-400/50 text-sky-300 bg-sky-400/10"
                  : "border-zinc-800 text-zinc-400"
              } ${creating ? "opacity-50" : ""}`}
            >
              {newF.autofill ? "Yes — fetch posts + voice" : "Skip (manual)"}
            </button>
          </div>
        </div>

        {isCompany && newF.autofill && (
          <div className="mt-3 surface border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-200/90 flex items-start gap-2">
            <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              Company pages (<span className="mono">/company/…</span>) often return fewer or no
              posts via Apify. We'll try — if it comes back empty, you can paste 3-5 sample posts
              manually after creation.
            </span>
          </div>
        )}

        <div className="mt-4">
          <Button onClick={create} disabled={creating} className="btn-primary" data-testid="vp-create">
            {creating ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Creating…
              </>
            ) : (
              <>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add voice profile
              </>
            )}
          </Button>
        </div>

        {creating && (
          <div
            data-testid="vp-creating-progress"
            className="mt-4 surface p-4 fade-up border-sky-400/30 bg-sky-400/5"
          >
            <div className="flex items-center gap-3 mb-3">
              <Loader2 className="w-4 h-4 text-sky-400 animate-spin shrink-0" />
              <div className="overline text-sky-400" style={{ fontSize: 9 }}>
                analyzing — takes 30-60 seconds
              </div>
            </div>
            <div className="space-y-1.5">
              {STEPS.map((s, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-2 text-xs transition-colors ${
                    i < creatingStep
                      ? "text-emerald-400"
                      : i === creatingStep
                      ? "text-zinc-100"
                      : "text-zinc-600"
                  }`}
                >
                  <span className="w-4 inline-flex justify-center">
                    {i < creatingStep ? (
                      <Check className="w-3 h-3" />
                    ) : i === creatingStep ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                    )}
                  </span>
                  {s}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {profiles.map((vp) => (
          <VoiceProfileCard
            key={vp.id}
            workspaceId={workspaceId}
            vp={vp}
            onChange={load}
          />
        ))}
      </div>
    </div>
  );
}

function VoiceProfileCard({ workspaceId, vp, onChange }) {
  const [edit, setEdit] = useState(false);
  const [notes, setNotes] = useState(vp.voice_notes || "");
  const [distilling, setDistilling] = useState(false);
  const [suggested, setSuggested] = useState("");

  const save = async () => {
    await apiPatch(`/workspaces/${workspaceId}/voice_profiles/${vp.id}`, { voice_notes: notes });
    toast.success("Voice notes saved");
    setEdit(false);
    onChange();
  };

  const remove = async () => {
    if (!window.confirm(`Delete voice profile "${vp.name}"? Samples will be reassigned to default.`)) return;
    try {
      await apiDelete(`/workspaces/${workspaceId}/voice_profiles/${vp.id}`);
      toast.success("Removed");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const distill = async () => {
    setDistilling(true);
    try {
      const r = await apiPost(`/workspaces/${workspaceId}/voice_profiles/${vp.id}/distill`, {});
      setSuggested(r.suggested_voice_notes || "");
      toast.success(`Analyzed ${r.used_count} drafts with feedback/edits`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Need more feedback first");
    } finally {
      setDistilling(false);
    }
  };

  const applySuggested = async () => {
    await apiPost(`/workspaces/${workspaceId}/voice_profiles/${vp.id}/apply-distilled`, {
      voice_notes: suggested,
    });
    setNotes(suggested);
    setSuggested("");
    toast.success("Voice updated");
    onChange();
  };

  return (
    <div className="surface p-5 fade-up" data-testid={`vp-card-${vp.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          {vp.avatar_url ? (
            <img src={vp.avatar_url} alt={vp.name} className="w-10 h-10 rounded-md object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-md bg-zinc-800 flex items-center justify-center text-sm text-zinc-300">
              {(vp.name || "?")[0]?.toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="heading text-lg text-zinc-100">{vp.name}</span>
              {vp.is_default && (
                <span className="overline text-sky-400" style={{ fontSize: 9 }}>
                  default
                </span>
              )}
            </div>
            {vp.headline && <div className="text-xs text-zinc-500 truncate">{vp.headline}</div>}
            <div className="text-xs text-zinc-500 mt-0.5">
              {vp.samples_count ?? 0} samples · {vp.feedback_count ?? 0} feedback signals
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={distill}
            disabled={distilling}
            className="btn-ghost"
            data-testid={`vp-distill-${vp.id}`}
          >
            <Sparkles className={`w-3.5 h-3.5 mr-1 ${distilling ? "animate-pulse" : ""}`} />
            {distilling ? "Distilling…" : "Refine from feedback"}
          </Button>
          {!vp.is_default && (
            <button
              onClick={remove}
              data-testid={`vp-delete-${vp.id}`}
              className="text-zinc-500 hover:text-red-400 p-1"
              aria-label="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="overline mb-2" style={{ fontSize: 9 }}>
        voice notes
      </div>
      {edit ? (
        <>
          <Textarea
            data-testid={`vp-notes-${vp.id}`}
            rows={6}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="bg-zinc-950 border-zinc-800 text-zinc-100 mb-2"
          />
          <div className="flex items-center gap-2">
            <Button onClick={save} className="btn-primary" data-testid={`vp-save-${vp.id}`}>
              <Save className="w-3.5 h-3.5 mr-1" /> Save
            </Button>
            <button
              onClick={() => {
                setEdit(false);
                setNotes(vp.voice_notes || "");
              }}
              className="btn-ghost"
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="bg-zinc-950/60 border border-zinc-800 rounded-md px-4 py-3 text-sm text-zinc-300 whitespace-pre-wrap min-h-[60px]">
            {vp.voice_notes || <span className="text-zinc-600">No notes yet — write or distill from feedback.</span>}
          </div>
          <button onClick={() => setEdit(true)} className="btn-ghost mt-2 text-xs" data-testid={`vp-edit-${vp.id}`}>
            Edit notes
          </button>
        </>
      )}

      {suggested && (
        <div className="mt-4 surface p-4 border-sky-400/40 bg-sky-400/5" data-testid={`vp-suggested-${vp.id}`}>
          <div className="overline mb-2 text-sky-400" style={{ fontSize: 9 }}>
            suggested voice (from your feedback)
          </div>
          <div className="text-sm text-zinc-200 whitespace-pre-wrap mb-3">{suggested}</div>
          <div className="flex items-center gap-2">
            <Button onClick={applySuggested} className="btn-primary" data-testid={`vp-apply-${vp.id}`}>
              <Check className="w-3.5 h-3.5 mr-1" /> Apply
            </Button>
            <button onClick={() => setSuggested("")} className="btn-ghost text-xs">
              Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SignalsSection({ workspaceId }) {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ signal_keyword: "", signal_context: "", feature_to_mention: "" });

  const load = () =>
    apiGet(`/workspaces/${workspaceId}/icp_signals`).then(setItems).catch(() => setItems([]));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const add = async () => {
    if (!f.signal_keyword.trim() || !f.feature_to_mention.trim()) {
      toast.error("Keyword + feature required");
      return;
    }
    await apiPost(`/workspaces/${workspaceId}/icp_signals`, f);
    setF({ signal_keyword: "", signal_context: "", feature_to_mention: "" });
    toast.success("Signal added");
    load();
  };

  const remove = async (id) => {
    await apiDelete(`/workspaces/${workspaceId}/icp_signals/${id}`);
    load();
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="surface p-5">
        <div className="overline mb-3">add ICP signal</div>
        <p className="text-xs text-zinc-400 mb-4 leading-relaxed max-w-2xl">
          When a prospect's post contains <span className="text-zinc-200">this keyword</span>, the
          AI will know to weave in <span className="text-zinc-200">this product angle</span> — only
          if it fits naturally.
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Signal keyword / phrase">
            <Input
              data-testid="signal-keyword"
              value={f.signal_keyword}
              onChange={(e) => setF({ ...f, signal_keyword: e.target.value })}
              placeholder="root cause analysis"
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
            />
          </Field>
          <Field label="Context (optional)">
            <Input
              data-testid="signal-context"
              value={f.signal_context}
              onChange={(e) => setF({ ...f, signal_context: e.target.value })}
              placeholder="When discussed re: downtime"
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
            />
          </Field>
          <Field label="Feature / angle to mention">
            <Input
              data-testid="signal-feature"
              value={f.feature_to_mention}
              onChange={(e) => setF({ ...f, feature_to_mention: e.target.value })}
              placeholder="Searchable failure history"
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
            />
          </Field>
        </div>
        <Button onClick={add} className="btn-primary mt-4" data-testid="signal-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add signal
        </Button>
      </div>

      <div className="surface overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-zinc-800 overline">
          <div className="col-span-4">signal</div>
          <div className="col-span-4">angle</div>
          <div className="col-span-3">context</div>
          <div className="col-span-1"></div>
        </div>
        {items.length === 0 ? (
          <div className="px-5 py-8 text-sm text-zinc-500 text-center">
            No signals yet. Build the ICP Signal Map to teach the AI when to mention your product.
          </div>
        ) : (
          items.map((it) => (
            <div
              key={it.id}
              data-testid={`signal-row-${it.id}`}
              className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-zinc-800/60 text-sm items-center"
            >
              <div className="col-span-4 text-zinc-100 mono text-xs truncate">
                {it.signal_keyword}
              </div>
              <div className="col-span-4 text-zinc-300 truncate">{it.feature_to_mention}</div>
              <div className="col-span-3 text-zinc-500 truncate">{it.signal_context || "—"}</div>
              <div className="col-span-1 text-right">
                <button
                  onClick={() => remove(it.id)}
                  data-testid={`signal-delete-${it.id}`}
                  className="text-zinc-500 hover:text-red-400"
                  aria-label="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function KnowledgeSection({ workspaceId }) {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ category: "product", content: "", source: "" });

  const load = () =>
    apiGet(`/workspaces/${workspaceId}/knowledge_base`).then(setItems).catch(() => setItems([]));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const add = async () => {
    if (!f.content.trim()) return;
    await apiPost(`/workspaces/${workspaceId}/knowledge_base`, f);
    setF({ category: "product", content: "", source: "" });
    load();
    toast.success("Knowledge added");
  };

  const remove = async (id) => {
    await apiDelete(`/workspaces/${workspaceId}/knowledge_base/${id}`);
    load();
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="surface p-5">
        <div className="overline mb-3">add knowledge</div>
        <div className="flex items-center gap-2 mb-3">
          <select
            data-testid="kb-category"
            value={f.category}
            onChange={(e) => setF({ ...f, category: e.target.value })}
            className="bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm rounded-md px-3 py-2"
          >
            <option value="product">product</option>
            <option value="competitor">competitor</option>
            <option value="industry">industry</option>
            <option value="objection">objection</option>
          </select>
          <Input
            data-testid="kb-source"
            placeholder="source (optional)"
            value={f.source}
            onChange={(e) => setF({ ...f, source: e.target.value })}
            className="bg-zinc-900 border-zinc-800 text-zinc-100 flex-1"
          />
        </div>
        <Textarea
          data-testid="kb-content"
          rows={4}
          value={f.content}
          onChange={(e) => setF({ ...f, content: e.target.value })}
          placeholder="Any fact, story, objection-handling, market context the AI should know…"
          className="bg-zinc-900 border-zinc-800 text-zinc-100 mb-3"
        />
        <Button onClick={add} className="btn-primary" data-testid="kb-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add entry
        </Button>
      </div>

      <div className="space-y-2">
        {items.length === 0 ? (
          <div className="surface p-8 text-center">
            <div className="overline">no knowledge entries</div>
          </div>
        ) : (
          items.map((it) => (
            <div key={it.id} className="surface p-4" data-testid={`kb-item-${it.id}`}>
              <div className="flex items-start justify-between mb-2">
                <span className="overline" style={{ fontSize: 9 }}>
                  {it.category}
                  {it.source ? ` · ${it.source}` : ""}
                </span>
                <button
                  onClick={() => remove(it.id)}
                  className="text-zinc-500 hover:text-red-400"
                  data-testid={`kb-delete-${it.id}`}
                  aria-label="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
                {it.content}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-zinc-300 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function ListEditor({ items, onChange, onAdd, onRemove, placeholder, testidPrefix }) {
  return (
    <div className="space-y-2">
      {items.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input
            data-testid={`${testidPrefix}-${i}`}
            value={v}
            onChange={(e) => onChange(i, e.target.value)}
            placeholder={placeholder}
            className="bg-zinc-900 border-zinc-800 text-zinc-100 flex-1"
          />
          {items.length > 1 && (
            <button
              onClick={() => onRemove(i)}
              className="btn-ghost"
              data-testid={`${testidPrefix}-remove-${i}`}
              aria-label="Remove"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      <button
        onClick={onAdd}
        className="btn-ghost inline-flex items-center gap-1.5"
        data-testid={`${testidPrefix}-add`}
      >
        <Plus className="w-3.5 h-3.5" /> Add
      </button>
    </div>
  );
}
